import WaitingIndicator from '@ui/waiting-indicator/waiting-indicator';
import CourseDetail from './sections/course-detail';
import NoCourse from './no-course-view';
import useProjectCourse from './hooks/use-project-course';
import CourseCertificate from './sections/course-certificate';
import useCourseRequirement from './hooks/use-course-requirement';
import CourseRequirement from './sections/course-requirement';
import { lazy, Suspense } from 'react';
import Container from '@ui/container/container';
import { ICourseCertificate } from '@/types/courses';

const CourseAddRequirementModal = lazy(() => import('./modals/course-add-requirement-modal'));
const CourseDeleteRequirementModal = lazy(() => import('./modals/course-delete-requirement-modal'));

const CourseMain = () => {
  const {
    course,
    setCourse,
    working,
    workingEvent,
    handleSave,
    handleDelete,
    courseCanBeAddedToEvent,
    initializeCourse,
  } = useProjectCourse();

  const {
    modalOpen,
    currentRequirement,
    requirementWorking,
    requirementToDelete,
    setCurrentRequirement,
    setRequirementToDelete,
    openRequirementModal,
    closeRequirementModal,
    handleCreateRequirement,
    handleUpdateRequirement,
    handleDeleteRequirement,
    handleEditRequirement,
    requirementToUpdate,
    canAddRequirement,
    defaultSettings,
    defaultNewRequirement,
    canSaveNewRequirement,
  } = useCourseRequirement({
    course,
    setCourse,
    workingEvent,
  });

  if (working && !course) {
    return (
      <div className="flex ai-center jc-center" style={{ height: 'calc(50vh)' }}>
        <WaitingIndicator />
      </div>
    );
  }

  if (!course && !working) {
    return (
      <NoCourse
        setCourse={setCourse}
        courseCanBeAddedToEvent={courseCanBeAddedToEvent}
        initializeCourse={initializeCourse}
        working={working}
      />
    );
  }

  const handleCertificateUpdate = (updatedCert: ICourseCertificate) => {
    if (course) {
      setCourse({
        ...course,
        certificate: updatedCert,
      });
    }
  };

  return (
    <>
      <Container className="flex-column gap-12">
        <CourseDetail handleSave={handleSave} course={course} onDelete={handleDelete} working={working} />
        <CourseRequirement
          course={course}
          workingEvent={workingEvent}
          working={working}
          handleEditRequirement={handleEditRequirement}
          setRequirementToDelete={setRequirementToDelete}
          canAddRequirement={canAddRequirement}
          openRequirementModal={openRequirementModal}
          defaultNewRequirement={defaultNewRequirement}
          defaultSettings={defaultSettings}
        />
      </Container>
      <CourseCertificate
        course={course}
        savingCertificate={false}
        workingCertificate={course?.certificate}
        onCertificateUpdate={handleCertificateUpdate}
      />
      <Suspense fallback={null}>
        {modalOpen && (
          <CourseAddRequirementModal
            requirementWorking={requirementWorking}
            open={modalOpen}
            onClose={closeRequirementModal}
            workingEvent={workingEvent}
            requirements={course?.requirements}
            newRequirement={currentRequirement}
            setNewRequirement={setCurrentRequirement}
            onCreateOrUpdateRequirement={requirementToUpdate ? handleUpdateRequirement : handleCreateRequirement}
            defaultSettings={defaultSettings}
            defaultNewRequirement={defaultNewRequirement}
            canSaveNewRequirement={canSaveNewRequirement}
          />
        )}
      </Suspense>
      <Suspense fallback={null}>
        {requirementToDelete && (
          <CourseDeleteRequirementModal
            requirementToDelete={requirementToDelete}
            setRequirementToDelete={setRequirementToDelete}
            course={course}
            deleteRequirement={handleDeleteRequirement}
            requirementWorking={requirementWorking}
          />
        )}
      </Suspense>
    </>
  );
};

export default CourseMain;
